import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import { eq } from 'tio-ui/utilities';
import {
  PhCheckCircle,
  PhHourglass,
  PhClock,
  PhListBullets,
  PhFlowArrow,
  PhBank,
  PhArrowCounterClockwise,
  PhWarningCircle,
} from 'ember-phosphor-icons';
import Timeline from 'tio-reimagined/components/timeline';
import Chip from 'tio-reimagined/components/chip';
import { formatCurrency, formatCycleDate, formatNumericDate } from 'tio-reimagined/format';
import type { ContributionPaymentRecord } from 'tio-employee/services/reimagined-data';

type PaymentStatus = ContributionPaymentRecord['status'];

function chipIntent(status: PaymentStatus): 'success' | 'primary' | 'danger' | 'default' {
  switch (status) {
    case 'sent':
    case 'resent':
      return 'success';
    case 'failed':
      return 'danger';
    case 'in_progress':
      return 'primary';
    case 'pending':
      return 'default';
  }
}

function chipLabelKey(status: PaymentStatus): string {
  switch (status) {
    case 'sent':
      return 'slr.reimagined.status_sent';
    case 'resent':
      return 'slr.reimagined.status_resent';
    case 'failed':
      return 'slr.reimagined.status_failed';
    case 'in_progress':
      return 'slr.reimagined.status_in_progress';
    case 'pending':
      return 'slr.reimagined.status_pending';
  }
}

/**
 * ContributionTimeline — Displays contribution payment history as a visual timeline or table
 *
 * Provides two views:
 * - Timeline: Visual vertical timeline with status nodes and payment details
 * - Table: Traditional table with benefit, paid date, servicer, amount columns
 *
 * @example
 * ```gts
 * <ContributionTimeline @payments={{this.payments}} />
 * ```
 */
interface ContributionTimelineSignature {
  Args: {
    /** Array of contribution payment records to display */
    payments: ContributionPaymentRecord[];
    /** Total number of payments across all pages (from JSONAPI meta) */
    totalCount?: number;
  };
  Element: HTMLDivElement;
}

export default class ContributionTimeline extends Component<ContributionTimelineSignature> {
  @tracked viewMode: 'timeline' | 'table' = 'timeline';

  get pastPayments() {
    return this.args.payments.filter((p) => !p.isFuture);
  }

  switchToTimeline = () => {
    this.viewMode = 'timeline';
  };

  switchToTable = () => {
    this.viewMode = 'table';
  };

  <template>
    <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/[0.04]" ...attributes>
      {{! Header with view toggle }}
      <div class="flex items-center justify-between p-5 pb-0">
        <div class="flex items-center gap-2">
          <PhBank @size={{22}} class="tio-fg-secondary" />
          <Heading @level={{3}}>{{t "slr.table.reimagined.payment_history"}}</Heading>
          {{#if this.pastPayments.length}}
            <Chip @intent="default" @size="sm">{{t
                "slr.reimagined.payments_chip"
                count=(if @totalCount @totalCount this.pastPayments.length)
              }}</Chip>
          {{/if}}
        </div>

        <div class="flex items-center bg-gray-100 rounded-lg p-0.5">
          <button
            type="button"
            class="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors
              {{if
                (eq this.viewMode 'timeline')
                'bg-white shadow-sm tio-fg-primary'
                'tio-fg-muted hover:tio-fg-secondary'
              }}"
            {{on "click" this.switchToTimeline}}
          >
            <PhFlowArrow @size={{16}} />
            {{t "slr.reimagined.timeline_view"}}
          </button>
          <button
            type="button"
            class="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors
              {{if
                (eq this.viewMode 'table')
                'bg-white shadow-sm tio-fg-primary'
                'tio-fg-muted hover:tio-fg-secondary'
              }}"
            {{on "click" this.switchToTable}}
          >
            <PhListBullets @size={{16}} />
            {{t "slr.reimagined.table_view"}}
          </button>
        </div>
      </div>

      <div class="p-5">
        {{#if this.pastPayments.length}}
          {{#if (eq this.viewMode "timeline")}}
            {{! Timeline View }}
            <Timeline
              @items={{this.pastPayments}}
              @lineClass="bg-violet-200"
              @connectorClass="left-[15px]"
            >
              <:node as |payment|>
                <div
                  class="w-8 h-8 rounded-full flex items-center justify-center
                    {{if
                      (eq payment.status 'sent')
                      'tio-bg-success-soft tio-fg-success'
                      (if
                        (eq payment.status 'resent')
                        'tio-bg-info-soft tio-fg-info'
                        (if
                          (eq payment.status 'failed')
                          'tio-bg-danger-soft tio-fg-danger'
                          (if
                            (eq payment.status 'in_progress')
                            'tio-bg-info-soft tio-fg-info'
                            'bg-gray-100 tio-fg-disabled'
                          )
                        )
                      )
                    }}"
                >
                  {{#if (eq payment.status "sent")}}
                    <PhCheckCircle @size={{18}} />
                  {{else if (eq payment.status "resent")}}
                    <PhArrowCounterClockwise @size={{18}} />
                  {{else if (eq payment.status "failed")}}
                    <PhWarningCircle @size={{18}} />
                  {{else if (eq payment.status "in_progress")}}
                    <PhHourglass @size={{18}} />
                  {{else}}
                    <PhClock @size={{18}} />
                  {{/if}}
                </div>
              </:node>
              <:content as |payment|>
                <div class="flex items-center justify-between py-1">
                  <div>
                    <div class="font-medium tio-fg-primary">
                      {{formatCycleDate payment.benefitCycle}}
                    </div>
                    <div class="flex items-center gap-2 mt-0.5">
                      {{#if payment.servicer}}
                        <Text @variant="muted" class="text-sm">{{payment.servicer}}</Text>
                        {{! template-lint-disable no-bare-strings }}
                        <span class="text-gray-300">·</span>
                      {{/if}}
                      <Text @variant="muted" class="text-sm">
                        {{if
                          payment.paidDate
                          (formatNumericDate payment.paidDate)
                          (t "slr.reimagined.status_pending")
                        }}
                      </Text>
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="font-semibold tio-fg-primary">{{formatCurrency
                        payment.amount
                      }}</div>
                    <Chip @intent={{chipIntent payment.status}} @size="sm" class="mt-0.5">
                      {{t (chipLabelKey payment.status)}}
                    </Chip>
                  </div>
                </div>
              </:content>
            </Timeline>
          {{else}}
            {{! Table View }}
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead>
                  <tr class="border-b tio-border-default">
                    <th class="text-left py-3 px-4 text-sm font-medium tio-fg-muted">{{t
                        "slr.reimagined.table_header_benefit"
                      }}</th>
                    <th class="text-left py-3 px-4 text-sm font-medium tio-fg-muted">{{t
                        "slr.reimagined.table_header_paid_date"
                      }}</th>
                    <th class="text-left py-3 px-4 text-sm font-medium tio-fg-muted">{{t
                        "slr.reimagined.table_header_servicer"
                      }}</th>
                    <th class="text-left py-3 px-4 text-sm font-medium tio-fg-muted">{{t
                        "slr.reimagined.table_header_status"
                      }}</th>
                    <th class="text-right py-3 px-4 text-sm font-medium tio-fg-muted">{{t
                        "slr.reimagined.table_header_amount"
                      }}</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                  {{#each this.pastPayments as |payment|}}
                    <tr class="hover:bg-gray-50 transition-colors">
                      <td class="py-3 px-4 text-sm tio-fg-primary">{{formatCycleDate
                          payment.benefitCycle
                        }}</td>
                      <td class="py-3 px-4 text-sm tio-fg-secondary">
                        {{if payment.paidDate (formatNumericDate payment.paidDate) "—"}}
                      </td>
                      <td class="py-3 px-4 text-sm tio-fg-secondary">{{if
                          payment.servicer
                          payment.servicer
                          "—"
                        }}</td>
                      <td class="py-3 px-4">
                        <Chip @intent={{chipIntent payment.status}} @size="sm">
                          {{t (chipLabelKey payment.status)}}
                        </Chip>
                      </td>
                      <td
                        class="py-3 px-4 text-sm font-medium tio-fg-primary text-right"
                      >{{formatCurrency payment.amount}}</td>
                    </tr>
                  {{/each}}
                </tbody>
              </table>
            </div>
          {{/if}}
        {{else}}
          {{! Empty State }}
          <div class="text-center py-8">
            <div
              class="p-4 bg-gray-50 rounded-full w-16 h-16 mx-auto mb-3 flex items-center justify-center"
            >
              <PhBank @size={{32}} class="tio-fg-disabled" />
            </div>
            <Text class="font-medium">{{t "slr.reimagined.no_payments_yet"}}</Text>
            <Text @variant="muted" class="text-sm mt-1">
              {{t "slr.reimagined.no_payments_description"}}
            </Text>
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}
